import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { config } from './env';

/**
 * Firebase is used for authentication and nothing else; Postgres owns all
 * Slate data. Two auth instances exist on purpose:
 *
 *   `auth`      the signed-in account (Google or email/password).
 *   `guestAuth` a *separate*, named Firebase app used only by /r/:token.
 *
 * Firebase keys its persisted session by `${apiKey}:${appName}`, so the guest
 * app has its own session. That matters: a signed-in Slate user who opens
 * somebody's request link must be able to hold an anonymous request-link
 * session without being signed out of their own account, and the API only
 * treats a caller as a `requestLink` actor when the ID token is anonymous.
 */

const GUEST_APP_NAME = 'slate-guest';

function appFor(name?: string): FirebaseApp {
  const existing = getApps().find((candidate) => candidate.name === (name ?? '[DEFAULT]'));
  if (existing) return name ? getApp(name) : getApp();
  return name ? initializeApp(config.firebase, name) : initializeApp(config.firebase);
}

export const firebaseApp: FirebaseApp = appFor();
export const auth: Auth = getAuth(firebaseApp);

let guestAuthInstance: Auth | null = null;

/** Lazily created so the ordinary app never pays for the guest session. */
export function getGuestAuth(): Auth {
  if (!guestAuthInstance) {
    guestAuthInstance = getAuth(appFor(GUEST_APP_NAME));
  }
  return guestAuthInstance;
}
